import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale } from "../../intl";
import {
  fetchCasaMapaPartnerCrmLeadExchange,
  rotateCasaMapaPartnerCrmInboundToken,
  rotateCasaMapaPartnerCrmOutboundSecret,
  testCasaMapaPartnerCrmOutbound,
  updateCasaMapaPartnerCrmLeadExchange,
} from "../../services/casamapaPartnerAgencyApi";
import PartnerCasaMapaSettingsShell from "./PartnerCasaMapaSettingsShell";
import {
  formatPartnerSettingsWhen,
  PARTNER_SETTINGS_COPY as COPY,
} from "./partnerCasaMapaSettingsShared";

export default function PartnerCasaMapaCrmSettingsPage() {
  const { t } = useLocale();
  const { path } = usePartnerPortalBase();

  const [crm, setCrm] = useState(null);
  const [standards, setStandards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [crmSaving, setCrmSaving] = useState(false);
  const [revealedSecrets, setRevealedSecrets] = useState({ outbound: null, inbound: null });
  const [outboundDraft, setOutboundDraft] = useState({
    enabled: false,
    webhookUrl: "",
    format: "midpoint_lead_v1",
  });
  const [inboundDraft, setInboundDraft] = useState({ enabled: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const crmBody = await fetchCasaMapaPartnerCrmLeadExchange();
      const settings = crmBody?.settings || null;
      setCrm(settings);
      setStandards(settings?.standards || null);
      setOutboundDraft({
        enabled: Boolean(settings?.outbound?.enabled),
        webhookUrl: settings?.outbound?.webhookUrl || "",
        format: settings?.outbound?.format || "midpoint_lead_v1",
      });
      setInboundDraft({ enabled: Boolean(settings?.inbound?.enabled) });
      if (settings?.outbound?.signingSecret) {
        setRevealedSecrets((prev) => ({ ...prev, outbound: settings.outbound.signingSecret }));
      }
      if (settings?.inbound?.token) {
        setRevealedSecrets((prev) => ({ ...prev, inbound: settings.inbound.token }));
      }
    } catch (err) {
      setError(err.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const sampleInbound = useMemo(
    () =>
      JSON.stringify(
        {
          schema: outboundDraft.format || "midpoint_lead_v1",
          lead: {
            firstName: "Ana",
            lastName: "García",
            email: "ana@example.com",
            phone: "+57 300 123 4567",
            leadType: "contact_agent",
            notes: "Interested in El Poblado 3BR",
            listingId: null,
          },
        },
        null,
        2
      ),
    [outboundDraft.format]
  );

  const saveCrm = async () => {
    setCrmSaving(true);
    setMessage("");
    setError("");
    try {
      const result = await updateCasaMapaPartnerCrmLeadExchange({
        outbound: outboundDraft,
        inbound: inboundDraft,
      });
      setCrm(result.settings);
      setStandards(result.settings?.standards || standards);
      if (result.settings?.outbound?.signingSecret) {
        setRevealedSecrets((prev) => ({
          ...prev,
          outbound: result.settings.outbound.signingSecret,
        }));
      }
      if (result.settings?.inbound?.token) {
        setRevealedSecrets((prev) => ({ ...prev, inbound: result.settings.inbound.token }));
      }
      setMessage(result.message || t(`${COPY}.crm.saved`));
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setCrmSaving(false);
    }
  };

  const rotateOutbound = async () => {
    setCrmSaving(true);
    setError("");
    try {
      const result = await rotateCasaMapaPartnerCrmOutboundSecret();
      setCrm(result.settings);
      setRevealedSecrets((prev) => ({
        ...prev,
        outbound: result.settings?.outbound?.signingSecret || null,
      }));
      setMessage(result.message || t(`${COPY}.crm.secretRotated`));
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setCrmSaving(false);
    }
  };

  const rotateInbound = async () => {
    setCrmSaving(true);
    setError("");
    try {
      const result = await rotateCasaMapaPartnerCrmInboundToken();
      setCrm(result.settings);
      setInboundDraft({ enabled: true });
      setRevealedSecrets((prev) => ({
        ...prev,
        inbound: result.settings?.inbound?.token || null,
      }));
      setMessage(result.message || t(`${COPY}.crm.tokenRotated`));
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setCrmSaving(false);
    }
  };

  const testOutbound = async () => {
    setCrmSaving(true);
    setError("");
    try {
      const result = await testCasaMapaPartnerCrmOutbound();
      if (result.ok) setMessage(result.message || t(`${COPY}.crm.testOk`));
      else setError(result.message || t(`${COPY}.crm.testFail`));
      await load();
    } catch (err) {
      setError(err.message || t(`${COPY}.crm.testFail`));
    } finally {
      setCrmSaving(false);
    }
  };

  if (loading && !crm) {
    return <p className="muted panel-card">{t(`${COPY}.loading`)}</p>;
  }

  return (
    <PartnerCasaMapaSettingsShell
      title={t(`${COPY}.crm.title`)}
      subtitle={t(`${COPY}.crm.subtitle`)}
      message={message}
      error={error}
    >
      <section className="cm-partner-settings__section">
        <p className="cm-partner-settings__muted">
          {t(`${COPY}.crm.standardsNote`, {
            transport: standards?.transport || "HTTPS JSON webhooks",
          })}
        </p>

        <div className="cm-partner-settings__grid">
          <article className="cm-partner-settings__crm-card">
            <h3>{t(`${COPY}.crm.exposeTitle`)}</h3>
            <p>{t(`${COPY}.crm.exposeBody`)}</p>
            <label className="cm-partner-settings__toggle">
              <input
                type="checkbox"
                checked={outboundDraft.enabled}
                onChange={(event) =>
                  setOutboundDraft((prev) => ({ ...prev, enabled: event.target.checked }))
                }
              />
              {t(`${COPY}.crm.exposeEnable`)}
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.crm.webhookUrl`)}</span>
              <input
                value={outboundDraft.webhookUrl}
                onChange={(event) =>
                  setOutboundDraft((prev) => ({ ...prev, webhookUrl: event.target.value }))
                }
                placeholder="https://crm.example.com/hooks/casamapa"
                disabled={!outboundDraft.enabled}
              />
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.crm.format`)}</span>
              <select
                value={outboundDraft.format}
                onChange={(event) =>
                  setOutboundDraft((prev) => ({ ...prev, format: event.target.value }))
                }
                disabled={!outboundDraft.enabled}
              >
                {(standards?.formats || [
                  { id: "midpoint_lead_v1", label: "Midpoint Lead v1" },
                  { id: "reso_lead_lite", label: "RESO Lead (lite)" },
                ]).map((format) => (
                  <option key={format.id} value={format.id}>
                    {format.label}
                  </option>
                ))}
              </select>
            </label>
            {revealedSecrets.outbound ? (
              <div className="cm-partner-settings__secret">
                {t(`${COPY}.crm.signingSecretOnce`)}
                <code>{revealedSecrets.outbound}</code>
              </div>
            ) : crm?.outbound?.hasSigningSecret ? (
              <p className="cm-partner-settings__muted">{t(`${COPY}.crm.signingSecretSet`)}</p>
            ) : null}
            {crm?.outbound?.lastSuccessAt || crm?.outbound?.lastError ? (
              <p className="cm-partner-settings__muted">
                {crm.outbound.lastError
                  ? t(`${COPY}.crm.lastError`, { error: crm.outbound.lastError })
                  : t(`${COPY}.crm.lastSuccess`, {
                      when: formatPartnerSettingsWhen(crm.outbound.lastSuccessAt) || "—",
                    })}
              </p>
            ) : null}
            <div className="cm-partner-settings__actions">
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={crmSaving || !outboundDraft.enabled}
                onClick={testOutbound}
              >
                {t(`${COPY}.crm.testWebhook`)}
              </button>
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={crmSaving}
                onClick={rotateOutbound}
              >
                {t(`${COPY}.crm.rotateSecret`)}
              </button>
            </div>
          </article>

          <article className="cm-partner-settings__crm-card">
            <h3>{t(`${COPY}.crm.consumeTitle`)}</h3>
            <p>{t(`${COPY}.crm.consumeBody`)}</p>
            <label className="cm-partner-settings__toggle">
              <input
                type="checkbox"
                checked={inboundDraft.enabled}
                onChange={(event) => setInboundDraft({ enabled: event.target.checked })}
              />
              {t(`${COPY}.crm.consumeEnable`)}
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.crm.ingestUrl`)}</span>
              <code className="cm-partner-settings__code">
                {typeof window !== "undefined"
                  ? `${window.location.origin}${crm?.inbound?.ingestPath || "/api/v1/casamapa/partner-crm/inbound/leads"}`
                  : crm?.inbound?.ingestPath || "/api/v1/casamapa/partner-crm/inbound/leads"}
              </code>
            </label>
            {revealedSecrets.inbound ? (
              <div className="cm-partner-settings__secret">
                {t(`${COPY}.crm.inboundTokenOnce`)}
                <code>{revealedSecrets.inbound}</code>
              </div>
            ) : crm?.inbound?.hasToken ? (
              <p className="cm-partner-settings__muted">
                {t(`${COPY}.crm.inboundTokenSet`, {
                  prefix: crm.inbound.tokenPrefix || "mk_crm_…",
                })}
              </p>
            ) : null}
            <p className="cm-partner-settings__muted">{t(`${COPY}.crm.inboundAuthHint`)}</p>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.crm.samplePayload`)}</span>
              <pre className="cm-partner-settings__code">{sampleInbound}</pre>
            </label>
            {crm?.inbound?.lastReceivedAt ? (
              <p className="cm-partner-settings__muted">
                {t(`${COPY}.crm.inboundStats`, {
                  count: crm.inbound.receiveCount || 0,
                  when: formatPartnerSettingsWhen(crm.inbound.lastReceivedAt) || "—",
                })}
              </p>
            ) : null}
            <div className="cm-partner-settings__actions">
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={crmSaving}
                onClick={rotateInbound}
              >
                {t(`${COPY}.crm.rotateToken`)}
              </button>
              <Link className="partner-dashboard-btn" to={path("leads")}>
                {t(`${COPY}.crm.openLeads`)}
              </Link>
            </div>
          </article>
        </div>

        <div className="cm-partner-settings__actions">
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            disabled={crmSaving}
            onClick={saveCrm}
          >
            {crmSaving ? t(`${COPY}.saving`) : t(`${COPY}.crm.save`)}
          </button>
        </div>
      </section>
    </PartnerCasaMapaSettingsShell>
  );
}
